import { experienceDNAFromFivePointScale } from './experienceDNA';

const storySlug = (post = {}) => post.slug || String(post.title || 'story')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

const destinationLabel = (gallery = {}) => {
  if (typeof gallery.destination === 'string') return gallery.destination;
  if (gallery.destination) {
    return [gallery.destination.city, gallery.destination.region, gallery.destination.country]
      .filter(Boolean)
      .join(', ');
  }
  return gallery.tripCard?.destination || '';
};

const stat = (id, label, value, icon) => ({
  id, label, value, icon,
});

const essentialsFor = (gallery) => [
  stat('destination', gallery.destinationStatLabel || 'Destination', destinationLabel(gallery), 'mdi-map-marker-outline'),
  stat('dates', 'Dates', gallery.dateLabel || gallery.tripCard?.date, 'mdi-calendar-range'),
  stat('trip-type', 'Trip type', gallery.tripType || gallery.tripCard?.tripType || 'Corporate Retreat', 'mdi-compass-outline'),
  stat('family', 'Family included', gallery.familyFriendly ? 'Yes' : 'No', 'mdi-human-male-female-child'),
].filter((item) => item.value !== undefined && item.value !== null && item.value !== '');

const numberStatsFor = (gallery, publishedStoryCount) => [
  stat('stories', 'Chapters released', publishedStoryCount, 'mdi-book-open-page-variant-outline'),
  stat('activities', 'Featured activities', gallery.tripCard?.featuredActivities?.length, 'mdi-hiking'),
  stat('meals', 'Featured meals', gallery.tripCard?.featuredMeals?.length, 'mdi-silverware-fork-knife'),
  stat('achievements', 'Achievements', gallery.achievements?.length, 'mdi-medal-outline'),
].filter((item) => Number.isFinite(item.value) && item.value > 0);

const mediaFor = (gallery = {}) => (gallery.media || []).filter(Boolean).map((asset, index) => ({
  ...asset,
  id: asset.id || `${gallery.slug}-media-${index + 1}`,
}));

const activitiesFor = (gallery = {}) => (gallery.achievements || []).map((achievement, index) => ({
  id: achievement.slug,
  entityId: achievement.slug,
  name: achievement.title,
  title: achievement.title,
  category: achievement.category,
  meritBadgeSlug: achievement.slug,
  relatedBadgeIds: [achievement.slug],
  order: index + 1,
  eventDescription: `${achievement.title} was one of the shared activities that defined this Reckon retreat.`,
  cardImage: {
    id: `${achievement.slug}-activity-card`,
    src: achievement.image || gallery.heroImage,
    alt: achievement.image
      ? `${achievement.title} merit badge`
      : `${achievement.title} during ${gallery.title}`,
  },
}));

export const isCorporateRetreatGallery = (gallery) => gallery?.meta === 'Corporate Retreat'
  || gallery?.tripType === 'Corporate Retreat';

export const adaptCorporateRetreat = (gallery) => {
  if (!isCorporateRetreatGallery(gallery)) return null;

  const posts = gallery.blogPosts || [];
  const releasedPosts = gallery.storiesComingSoon
    ? []
    : posts.filter((post) => post.released !== false);
  const chapters = posts.map((post, index) => ({
    id: storySlug(post),
    slug: storySlug(post),
    chapterNumber: index + 1,
    title: post.title,
    subtitle: post.lede,
    summary: post.lede,
    dayLabel: post.dayLabel || `Chapter ${index + 1}`,
    status: post.released === false || gallery.storiesComingSoon ? 'draft' : 'published',
    route: {
      name: 'reckon-way-story',
      params: { gallerySlug: gallery.slug, storySlug: storySlug(post) },
    },
  }));
  const prologue = gallery.prologueStory ? {
    ...gallery.prologueStory,
    status: 'published',
  } : null;

  return {
    id: gallery.slug,
    slug: gallery.slug,
    title: gallery.title,
    subtitle: gallery.subtitle || gallery.tripCard?.subtitle,
    eventType: 'retreat',
    classification: 'Corporate Retreat',
    status: 'published',
    destination: destinationLabel(gallery),
    yearsLabel: gallery.dateLabel || gallery.tripCard?.date,
    heroImage: gallery.heroImage ? {
      id: `${gallery.slug}-hero`,
      src: gallery.heroImage,
      alt: `${gallery.title} hero image`,
    } : null,
    heroTags: (gallery.tags || []).slice(0, 5).map((tag) => tag.label),
    summary: gallery.summary || gallery.tripCard?.summary || gallery.description,
    atAGlance: essentialsFor(gallery),
    tripByNumbers: numberStatsFor(gallery, releasedPosts.length),
    experienceDNA: experienceDNAFromFivePointScale(gallery.experienceDNA),
    chapters,
    journeyStrip: gallery.journeyDays || [],
    travelStories: [prologue, ...chapters]
      .filter((chapter) => chapter && chapter.status === 'published'),
    places: [],
    restaurants: [],
    hotels: [],
    activities: activitiesFor(gallery),
    crew: gallery.attendees || [],
    meritBadges: [],
    gallery: mediaFor(gallery),
  };
};
